import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs?v=20260804`;

type CourseItem = {
  id: number;
  category: string;
  name: string;
  instructor: string;
  department: string;
  assigned_user: string;
  status: "published" | "private";
  students: number;
  thumbnail?: string | null;
  video_url?: string | null;
  ppt_url?: string | null;
  ppt_preview_url?: string | null;
  ppt_slide_urls?: string | null;
  pdf_url?: string | null;
  start_date?: string | null;
  end_date?: string | null;
};

type LoggedInUser = {
  id?: number;
  username?: string;
  email?: string;
  department?: string;
  role?: "employee" | "admin";
};

type CourseProgress = {
  id?: number;
  user_id: number;
  course_id: number;
  video_progress: number;
  pdf_progress: number;
  ppt_progress: number;
  final_progress: number;
  video_completed: boolean;
  pdf_completed: boolean;
  ppt_completed: boolean;
  course_completed: boolean;
  video_last_position: number;
ppt_last_page: number;
pdf_last_page: number;
};

function EmployeeCourseLearningPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [course, setCourse] = useState<CourseItem | null>(null);
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser | null>(null);
  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasFinalTest, setHasFinalTest] = useState(false);
  const [pptPageNumber, setPptPageNumber] = useState(1);
  const [pptNumPages, setPptNumPages] = useState<number | null>(null);

  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState<number | null>(null);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("loggedInUser");

    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setLoggedInUser(parsedUser);
      fetchCourse(parsedUser);
    } else {
      fetchCourse(null);
    }
  }, [courseId]);

  const handleOpenFinalTest = () => {
    if (!course?.name) return;

    localStorage.setItem("employeeDashboardActiveMenu", "tests");
    localStorage.setItem("employeeAutoOpenTestCourseName", course.name);

    navigate("/employee-dashboard");
  };

  const fetchCourse = async (user: LoggedInUser | null) => {
    try {
      const response = await fetch("/courses/");
      const data: CourseItem[] = await response.json();

      const selectedCourse = data.find((item) => item.id === Number(courseId));
setCourse(selectedCourse || null);

if (selectedCourse && user?.id) {
  localStorage.setItem(
    `employeeLastLearningCourseId_${user.id}`,
    String(selectedCourse.id)
  );
}

if (selectedCourse) {
        const testsResponse = await fetch("/tests/");
        const testsData = await testsResponse.json();

        const courseHasTest = testsData.some(
          (test: any) =>
            test.status === "published" &&
            test.course_name === selectedCourse.name
        );

        setHasFinalTest(courseHasTest);
      }

      if (user?.id && selectedCourse?.id) {
        await fetchProgress(user.id, selectedCourse.id);
      }
    } catch (error) {
      console.error("Course fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async (userId: number, selectedCourseId: number) => {
    try {
      const response = await fetch(
        `/course-progress/${userId}/${selectedCourseId}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch course progress");
      }

      const data = await response.json();
      setProgress(data);
    } catch (error) {
      console.error("Progress fetch error:", error);
    }
  };

  const updateProgress = async (
  nextVideoProgress: number,
  nextPdfProgress: number,
  nextPptProgress: number,
  nextVideoLastPosition?: number,
  nextPdfLastPage?: number,
  nextPptLastPage?: number
) => {
  if (!loggedInUser?.id || !course?.id) return;

  try {
    const response = await fetch(
      "/course-progress/",
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: loggedInUser.id,
          course_id: course.id,
          video_progress: Math.max(
            progress?.video_progress || 0,
            nextVideoProgress
          ),
          pdf_progress: Math.max(
            progress?.pdf_progress || 0,
            nextPdfProgress
          ),
          ppt_progress: Math.max(
            progress?.ppt_progress || 0,
            nextPptProgress
          ),
          video_last_position:
            nextVideoLastPosition !== undefined
              ? nextVideoLastPosition
              : progress?.video_last_position || 0,
          pdf_last_page:
            nextPdfLastPage !== undefined
              ? nextPdfLastPage
              : progress?.pdf_last_page || 1,
          ppt_last_page:
            nextPptLastPage !== undefined
              ? nextPptLastPage
              : progress?.ppt_last_page || 1,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to update course progress");
    }

    const data = await response.json();
    setProgress(data);
  } catch (error) {
    console.error("Progress update error:", error);
  }
};

const handleVideoTimeUpdate = () => {
  const video = videoRef.current;

  if (!video || !video.duration || Number.isNaN(video.duration)) return;

  const watchedPercent = Math.round(
    (video.currentTime / video.duration) * 100
  );

  if (watchedPercent > (progress?.video_progress || 0)) {
    updateProgress(
      watchedPercent,
      progress?.pdf_progress || 0,
      progress?.ppt_progress || 0,
      video.currentTime,
      progress?.pdf_last_page || 1,
      progress?.ppt_last_page || 1
    );
  }
};

const handleVideoEnded = () => {
  const video = videoRef.current;

  updateProgress(
    100,
    progress?.pdf_progress || 0,
    progress?.ppt_progress || 0,
    video?.duration || progress?.video_last_position || 0,
    progress?.pdf_last_page || 1,
    progress?.ppt_last_page || 1
  );
};

const handlePdfOpen = () => {
  updateProgress(
    progress?.video_progress || 0,
    100,
    progress?.ppt_progress || 0,
    progress?.video_last_position || 0,
    progress?.pdf_last_page || 1,
    progress?.ppt_last_page || 1
  );
};

const updatePdfProgressByPage = (
  pageNumber: number,
  totalPages: number | null
) => {
  if (!totalPages || totalPages <= 0) return;

  const nextPdfProgress = Math.round(
    (pageNumber / totalPages) * 100
  );

  updateProgress(
    progress?.video_progress || 0,
    nextPdfProgress,
    progress?.ppt_progress || 0,
    progress?.video_last_position || 0,
    pageNumber,
    progress?.ppt_last_page || 1
  );
};

const handlePdfDocumentLoadSuccess = ({
  numPages,
}: {
  numPages: number;
}) => {
  setPdfNumPages(numPages);

  const savedPage =
    progress?.pdf_last_page && progress.pdf_last_page <= numPages
      ? progress.pdf_last_page
      : 1;

  setPdfPageNumber(savedPage);
  updatePdfProgressByPage(savedPage, numPages);
};

const updatePptProgressByPage = (
  pageNumber: number,
  totalPages: number | null
) => {
  if (!totalPages || totalPages <= 0) return;

  const nextPptProgress = Math.round(
    (pageNumber / totalPages) * 100
  );

  updateProgress(
    progress?.video_progress || 0,
    progress?.pdf_progress || 0,
    nextPptProgress,
    progress?.video_last_position || 0,
    progress?.pdf_last_page || 1,
    pageNumber
  );
};

const handlePptDocumentLoadSuccess = ({
  numPages,
}: {
  numPages: number;
}) => {
  setPptNumPages(numPages);

  const savedPage =
    progress?.ppt_last_page && progress.ppt_last_page <= numPages
      ? progress.ppt_last_page
      : 1;

  setPptPageNumber(savedPage);
  updatePptProgressByPage(savedPage, numPages);
};

  const getMaterialProgress = (value?: number) => {
    return Math.round(value || 0);
  };

  if (loading) {
    return <div style={pageStyle}>読み込み中...</div>;
  }

  if (!course) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>コースが見つかりません。</div>
      </div>
    );
  }

  const videoProgress = getMaterialProgress(progress?.video_progress);
  const pdfProgress = getMaterialProgress(progress?.pdf_progress);
  const pptProgress = getMaterialProgress(progress?.ppt_progress);
  const finalProgress = getMaterialProgress(progress?.final_progress);

  return (
    <div style={pageStyle}>
      <button
  type="button"
  onClick={() => navigate("/employee-my-courses")}
  style={backButtonStyle}
>
  ← マイコースへ戻る
</button>

      <div style={headerCardStyle}>
        <h1 style={titleStyle}>{course.name}</h1>

        <p style={subtitleStyle}>
          講師：{course.instructor || "-"} / カテゴリー：{course.category || "-"}
        </p>

        <div style={progressSummaryStyle}>
          <div style={progressHeaderRowStyle}>
            <span style={progressLabelStyle}>総合進捗</span>
            <span style={progressPercentStyle}>{finalProgress}%</span>
          </div>

          <div style={progressBarBgStyle}>
            <div style={{ ...progressBarFillStyle, width: `${finalProgress}%` }} />
          </div>
        </div>

        <div style={materialProgressGridStyle}>
          {course.video_url && (
            <ProgressMiniCard label="動画" value={videoProgress} />
          )}

          {course.pdf_url && <ProgressMiniCard label="PDF" value={pdfProgress} />}

          {course.ppt_url && <ProgressMiniCard label="PPT" value={pptProgress} />}
        </div>

        {finalProgress >= 100 && hasFinalTest && (
          <div style={completionCardStyle}>
            <div>
              <h2 style={completionTitleStyle}>コース完了</h2>
              <p style={completionTextStyle}>
                このコースの学習が完了しました。最終テストを開始できます。
              </p>
            </div>

            <button
              type="button"
              onClick={handleOpenFinalTest}
              style={finalTestButtonStyle}
            >
              最終テストを開始
            </button>
          </div>
        )}
      </div>

      {course.video_url && (
        <div style={cardStyle}>
          <h2 style={sectionTitleStyle}>動画</h2>

          <div style={singleProgressBoxStyle}>動画進捗：{videoProgress}%</div>

          <video
  ref={videoRef}
  controls
  style={videoStyle}
  onTimeUpdate={handleVideoTimeUpdate}
  onEnded={handleVideoEnded}
  onLoadedMetadata={() => {
    const video = videoRef.current;

    if (
      video &&
      progress?.video_last_position &&
      progress.video_last_position > 0 &&
      progress.video_last_position < video.duration
    ) {
      video.currentTime = progress.video_last_position;
    }
  }}
>
            <source src={course.video_url} />
            お使いのブラウザは動画再生に対応していません。
          </video>
        </div>
      )}


      {course.pdf_url && (
  <div style={cardStyle}>
    <h2 style={sectionTitleStyle}>PDF資料</h2>

    <div style={singleProgressBoxStyle}>
      PDF進捗：{pdfProgress}%
    </div>

    <div style={pptViewerStyle}>
      <Document
        file={course.pdf_url}
        onLoadSuccess={handlePdfDocumentLoadSuccess}
        loading={<div style={noteStyle}>PDFを読み込み中...</div>}
      >
        <Page
          pageNumber={pdfPageNumber}
          width={900}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      </Document>

      <div style={pptControlBarStyle}>
        <button
          type="button"
          onClick={() => {
            const nextPage = Math.max(
              pdfPageNumber - 1,
              1
            );

            setPdfPageNumber(nextPage);
            updatePdfProgressByPage(
              nextPage,
              pdfNumPages
            );
          }}
          disabled={pdfPageNumber <= 1}
          style={{
            ...pptNavButtonStyle,
            opacity: pdfPageNumber <= 1 ? 0.35 : 1,
          }}
        >
          ‹
        </button>

        <span style={pptPageCountStyle}>
          {pdfPageNumber} / {pdfNumPages || "-"}
        </span>

        <button
          type="button"
          onClick={() => {
            const nextPage = pdfNumPages
              ? Math.min(
                  pdfPageNumber + 1,
                  pdfNumPages
                )
              : pdfPageNumber;

            setPdfPageNumber(nextPage);

            updatePdfProgressByPage(
              nextPage,
              pdfNumPages
            );
          }}
          disabled={
            pdfNumPages !== null &&
            pdfPageNumber >= pdfNumPages
          }
          style={{
            ...pptNavButtonStyle,
            opacity:
              pdfNumPages !== null &&
              pdfPageNumber >= pdfNumPages
                ? 0.35
                : 1,
          }}
        >
          ›
        </button>
            </div>
    </div>

    <a
      href={course.pdf_url}
      target="_blank"
      rel="noreferrer"
      style={linkButtonStyle}
    >
       ダウンロード
    </a>
  </div>
)}

      

      {course.ppt_url && (
        <div style={cardStyle}>
          <h2 style={sectionTitleStyle}>PPT資料</h2>

          <div style={singleProgressBoxStyle}>PPT進捗：{pptProgress}%</div>

          {course.ppt_preview_url ? (
            <div style={pptViewerStyle}>
              <Document
                file={course.ppt_preview_url}
                onLoadSuccess={handlePptDocumentLoadSuccess}
                loading={<div style={noteStyle}>PPTを読み込み中...</div>}
                error={
                  <div style={noteStyle}>PPTプレビューを表示できません。</div>
                }
              >
                <Page
                  pageNumber={pptPageNumber}
                  width={900}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>

              <div style={pptControlBarStyle}>
                <button
                  type="button"
                  onClick={() => {
  const nextPage = Math.max(pptPageNumber - 1, 1);
  setPptPageNumber(nextPage);
  updatePptProgressByPage(nextPage, pptNumPages);
}}
                  disabled={pptPageNumber <= 1}
                  style={{
                    ...pptNavButtonStyle,
                    opacity: pptPageNumber <= 1 ? 0.35 : 1,
                  }}
                >
                  ‹
                </button>

                <span style={pptPageCountStyle}>
                  {pptPageNumber} / {pptNumPages || "-"}
                </span>

                <button
                  type="button"
                  onClick={() => {
  const nextPage = pptNumPages
    ? Math.min(pptPageNumber + 1, pptNumPages)
    : pptPageNumber;

  setPptPageNumber(nextPage);
  updatePptProgressByPage(nextPage, pptNumPages);
}}
                  disabled={pptNumPages !== null && pptPageNumber >= pptNumPages}
                  style={{
                    ...pptNavButtonStyle,
                    opacity:
                      pptNumPages !== null && pptPageNumber >= pptNumPages
                        ? 0.35
                        : 1,
                  }}
                >
                  ›
                </button>
              </div>
            </div>
          ) : (
            <div style={noteStyle}>PPTプレビューが登録されていません。</div>
          )}

          <a
            href={course.ppt_url}
            target="_blank"
            rel="noreferrer"
            style={linkButtonStyle}
            
          >
             ダウンロード
          </a>
        </div>
      )}

      {!course.video_url && !course.pdf_url && !course.ppt_url && (
        <div style={cardStyle}>このコースには表示できる教材がありません。</div>
      )}
    </div>
  );
}

function ProgressMiniCard({ label, value }: { label: string; value: number }) {
  return (
    <div style={miniCardStyle}>
      <div style={miniLabelStyle}>{label}</div>
      <div style={miniValueStyle}>{value}%</div>
      <div style={miniBarBgStyle}>
        <div style={{ ...miniBarFillStyle, width: `${value}%` }} />
      </div>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  backgroundColor: "#f8fafc",
  padding: "32px",
  color: "#0f172a",
};

const headerCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "20px",
  padding: "28px",
  marginBottom: "24px",
};

const cardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "20px",
  padding: "24px",
  marginBottom: "24px",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "30px",
  fontWeight: 900,
};

const subtitleStyle: React.CSSProperties = {
  marginTop: "10px",
  color: "#64748b",
  fontSize: "15px",
};

const sectionTitleStyle: React.CSSProperties = {
  marginTop: 0,
  marginBottom: "16px",
  fontSize: "22px",
  fontWeight: 900,
};

const progressSummaryStyle: React.CSSProperties = {
  marginTop: "24px",
};

const progressHeaderRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "10px",
};

const progressLabelStyle: React.CSSProperties = {
  fontSize: "16px",
  fontWeight: 900,
};

const progressPercentStyle: React.CSSProperties = {
  fontSize: "20px",
  fontWeight: 900,
  color: "#0f766e",
};

const progressBarBgStyle: React.CSSProperties = {
  width: "100%",
  height: "12px",
  backgroundColor: "#dbeafe",
  borderRadius: "999px",
  overflow: "hidden",
};

const progressBarFillStyle: React.CSSProperties = {
  height: "100%",
  backgroundColor: "#0f766e",
};

const materialProgressGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "14px",
  marginTop: "20px",
};

const miniCardStyle: React.CSSProperties = {
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "14px",
  padding: "14px",
};

const miniLabelStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 800,
  color: "#64748b",
};

const miniValueStyle: React.CSSProperties = {
  marginTop: "6px",
  fontSize: "22px",
  fontWeight: 900,
  color: "#0f172a",
};

const miniBarBgStyle: React.CSSProperties = {
  marginTop: "10px",
  height: "8px",
  backgroundColor: "#dbeafe",
  borderRadius: "999px",
  overflow: "hidden",
};

const miniBarFillStyle: React.CSSProperties = {
  height: "100%",
  backgroundColor: "#0f766e",
};

const singleProgressBoxStyle: React.CSSProperties = {
  display: "inline-block",
  backgroundColor: "#ccfbf1",
  color: "#0f766e",
  padding: "8px 14px",
  borderRadius: "999px",
  fontWeight: 900,
  marginBottom: "16px",
};

const videoStyle: React.CSSProperties = {
  width: "100%",
  maxHeight: "520px",
  borderRadius: "16px",
  backgroundColor: "#000000",
};

const iframeStyle: React.CSSProperties = {
  width: "100%",
  height: "650px",
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
};

const pptViewerStyle: React.CSSProperties = {
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "18px",
  padding: "24px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  overflowX: "auto",
};

const pptControlBarStyle: React.CSSProperties = {
  marginTop: "18px",
  width: "100%",
  maxWidth: "900px",
  backgroundColor: "rgba(15, 23, 42, 0.75)",
  color: "#ffffff",
  borderRadius: "12px",
  padding: "12px 18px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  boxSizing: "border-box",
};

const pptNavButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "transparent",
  color: "#ffffff",
  fontSize: "34px",
  fontWeight: 900,
  cursor: "pointer",
};

const pptPageCountStyle: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 900,
};

const noteStyle: React.CSSProperties = {
  backgroundColor: "#fff7ed",
  color: "#9a3412",
  padding: "12px 14px",
  borderRadius: "12px",
  fontWeight: 700,
  marginBottom: "14px",
};

const linkButtonStyle: React.CSSProperties = {
  display: "inline-block",
  marginTop: "16px",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "12px 18px",
  borderRadius: "12px",
  fontWeight: 800,
  textDecoration: "none",
};

const backButtonStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  backgroundColor: "#ffffff",
  color: "#0f172a",
  padding: "12px 18px",
  borderRadius: "12px",
  fontWeight: 800,
  cursor: "pointer",
  marginBottom: "20px",
};

const completionCardStyle: React.CSSProperties = {
  marginTop: "24px",
  backgroundColor: "#ecfdf5",
  border: "1px solid #99f6e4",
  borderRadius: "18px",
  padding: "22px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "18px",
};

const completionTitleStyle: React.CSSProperties = {
  margin: 0,
  color: "#0f766e",
  fontSize: "22px",
  fontWeight: 900,
};

const completionTextStyle: React.CSSProperties = {
  margin: "8px 0 0",
  color: "#047857",
  fontSize: "15px",
  fontWeight: 700,
};

const finalTestButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "13px 20px",
  borderRadius: "12px",
  fontWeight: 900,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

export default EmployeeCourseLearningPage;